import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { useLocalization, useTranslation } from '@rbx/intl';
import { Alert, Link, RefreshIcon } from '@rbx/ui';
import React, { FC, useEffect } from 'react';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { useProvidedConversationReducer, ConnectionState } from '@rbx/conv-ai-provider';
import useBotAlertStyles from './BotAlert.styles';

type ConnectionStateAlertProps = {
  connectionState: ConnectionState;
};

const ConnectionStateAlert: FC<React.PropsWithChildren<ConnectionStateAlertProps>> = ({
  connectionState,
}) => {
  const { translate, translateHTML } = useTranslation();
  const { locale } = useLocalization();
  const {
    classes: { alert },
  } = useBotAlertStyles();
  const {
    state: { conversationId },
  } = useProvidedConversationReducer();

  useEffect(() => {
    unifiedLoggerClient.logImpressionEvent({
      eventName: DocsAssistantEvent.ConnectionStateChangeAlert,
      parameters: {
        connectionState,
        conversationId: conversationId ?? '',
        locale: locale ? String(locale) : '',
      },
    });
  }, [locale, connectionState, conversationId]);

  switch (connectionState) {
    case ConnectionState.Closed:
      return (
        <Alert className={alert} severity='error'>
          {translateHTML('AlertMessage.ConnectionClosed', [
            {
              opening: 'linkStart',
              closing: 'linkEnd',
              // Use <Link> instead of <DynamicallyRouted.Link> because
              // the link is a relative path that should not be affected by the locale
              // eslint-disable-next-line react/no-unstable-nested-components
              content: (chunks) => (
                // eslint-disable-next-line react/forbid-component-props
                <Link href={`${window.location.pathname}${window.location.search}`}>{chunks}</Link>
              ),
            },
          ])}
        </Alert>
      );
    case ConnectionState.Reconnecting:
      return (
        <Alert
          aria-label={translate('Label.Alert')}
          className={alert}
          severity='warning'
          icon={<RefreshIcon />}>
          {translate('AlertMessage.Reconnecting')}
        </Alert>
      );
    default:
      return null;
  }
};

export default ConnectionStateAlert;
